import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Table, Spinner, Alert } from 'react-bootstrap';
import ConfigService from "../../services/config/ConfigService";
import LoadingSpinner from '../common/LoadingSpinner';
import './ConfigEdit.css';

const ConfigEdit = () => {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await ConfigService.getAllConfig();
            setConfigs(data);
        } catch (err) {
            setError('Failed to load configuration items: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const startEditing = (config) => {
        setEditingId(config.id);
        setEditValue(config.value || '');
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditValue('');
    };

    const saveConfig = async (config) => {
        try {
            setSaving(config.id);
            await ConfigService.saveConfigItem({
                ...config,
                value: editValue
            });
            // Update local state
            setConfigs(configs.map(c =>
                c.id === config.id ? { ...c, value: editValue } : c
            ));
            setEditingId(null);
            setEditValue('');
        } catch (err) {
            setError('Failed to save configuration: ' + err.message);
        } finally {
            setSaving(null);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    const renderBody = () => {
        return (
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Name</th>
                        <th>Value</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {configs.map((config) => (
                        <tr key={config.id}>
                            <td>{config.configType}</td>
                            <td>{config.name}</td>
                            <td>
                                {editingId === config.id ? (
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        autoFocus
                                    />
                                ) : (
                                    <span>{config.value}</span>
                                )}
                            </td>
                            <td>
                                {editingId === config.id ? (
                                    <>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={() => saveConfig(config)}
                                            disabled={saving === config.id}
                                            className="me-2"
                                        >
                                            {saving === config.id ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save'
                                            )}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={cancelEditing}
                                            disabled={saving === config.id}
                                        >
                                            Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => startEditing(config)}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    }
    return (
        <Container className="my-4">
            {/* MODERNIZED: Page is wrapped in a main Card for a modern look */}
            <Card className="h-100 shadow-sm">
                <Card.Header className="d-flex flex-column gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <h2 className="mb-0">Configuration Editor</h2>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    )}
                    {loading ? <div className='loadingSpinner'><LoadingSpinner /></div> : renderBody()}
                </Card.Body>
            </Card>
        </Container>
    );

};

export default ConfigEdit;
